import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import moment from 'moment';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import { makeStyles } from '@mui/styles';

import {
  Card,
  CardActions,
  CardContent,
  Avatar,
  Checkbox,
  Table,
  Grid,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
} from '@mui/material';

import { getInitials } from 'helpers';
import { NavLink } from 'react-router-dom';
import { updateSelectedUserIds } from 'store/actions/account'
import { useDispatch, useSelector } from 'react-redux';
import { ListCard, ListCardField, ListCards } from 'components';
import useCompactLayout from 'hooks/useCompactLayout';

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: 0
  },
  inner: {
   // minWidth: 1050
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  textLink: {
    textDecoration: 'underline',
  },
}));

const UsersTable = props => {
  const { className, users, ...rest } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const isCompact = useCompactLayout();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const updateUserIds = (ids) => dispatch(updateSelectedUserIds(ids));
  const userFilter = useSelector((state) => state.account.filter);

  const handleSelectAll = event => {
    const { users } = props;

    let selectedUsers;

    if (event.target.checked) {
      selectedUsers = (users || []).map(user => user.id);
    } else {
      selectedUsers = [];
    }

    setSelectedUsers(selectedUsers);
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedUsers.indexOf(id);
    let newSelectedUsers = [];

    if (selectedIndex === -1) {
      newSelectedUsers = newSelectedUsers.concat(selectedUsers, id);
    } else if (selectedIndex === 0) {
      newSelectedUsers = newSelectedUsers.concat(selectedUsers.slice(1));
    } else if (selectedIndex === selectedUsers.length - 1) {
      newSelectedUsers = newSelectedUsers.concat(selectedUsers.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedUsers = newSelectedUsers.concat(
        selectedUsers.slice(0, selectedIndex),
        selectedUsers.slice(selectedIndex + 1)
      );
    }

    setSelectedUsers(newSelectedUsers);
    updateUserIds(newSelectedUsers);
  };

  const handlePageChange = (event, page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
  };

  const filterUsers = () => {
    // Ensure users is an array
    if (!users || !Array.isArray(users)) {
      return [];
    }

    if (!userFilter || userFilter === null || userFilter.trim() === '') {
      return users;
    }

    const filtered = users.filter(user => 
      (user.name && user.name.toLowerCase().includes(userFilter.toLowerCase())) 
      || (user.restaurantId && user.restaurantId.toLowerCase().includes(userFilter.toLowerCase()))
      || (user.role && user.role.toLowerCase().includes(userFilter.toLowerCase()))
      || (user.email && user.email.toLowerCase().includes(userFilter.toLowerCase()))
    );
    
    return filtered || [];
  };

const getTimeString = (date) => {
  if (!date) {
    return ''
  }
  if ((typeof date === 'object') && '_seconds' in date) {
    return moment(new Date(date._seconds * 1000)).format('DD/MM/YYYY')
  }
  return moment(date).format('DD/MM/YYYY')
};

  // One slice, shared by both layouts, so page state survives a resize.
  const visibleUsers = filterUsers();
  const pagedUsers = visibleUsers.slice(page * rowsPerPage, rowsPerPage * page + rowsPerPage);

  const userRestaurants = (user) =>
    (user.restaurants || []).map((restaurant) => (
      <Typography key={`${restaurant.restaurantId}-${restaurant.name}`} variant="body2">
        {`${restaurant.name}-${restaurant.restaurantId}`}
      </Typography>
    ));

  const renderCards = () => (
    <ListCards count={visibleUsers.length} noun="user" empty="No users to show.">
      {pagedUsers.map((user) => (
        <ListCard
          key={user.username}
          data-testid="user-card"
          header={
            <>
              <Checkbox
                checked={selectedUsers.indexOf(user.username) !== -1}
                color="primary"
                onChange={(event) => handleSelectOne(event, user.username)}
                size="small"
                inputProps={{ 'aria-label': `select ${user.username}` }}
              />
              <Avatar className={classes.avatar} src={user.avatarUrl}>
                {getInitials(user.username)}
              </Avatar>
              <NavLink to={`/user/${user.username}`}>
                <Typography variant="body1" className={classes.textLink}>{user.username}</Typography>
              </NavLink>
            </>
          }
        >
          <ListCardField label="Email" value={user.email} />
          <ListCardField label="Role" value={user.role} />
          <ListCardField
            label="Restaurant"
            value={
              <>
                <Typography color="primary" variant="body2">{user.restaurantId}</Typography>
                {userRestaurants(user)}
              </>
            }
          />
          <ListCardField label="Phone" value={user.phone} />
          <ListCardField label="Registered" value={getTimeString(user.createdAt)} />
        </ListCard>
      ))}
    </ListCards>
  );

  return (
    <Grid
      item
      lg={12}
      md={12}
      xl={12}
      xs={12}
    >
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <CardContent className={classes.content}>
        {isCompact ? renderCards() : (
          <div className={classes.inner}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.length === (users?.length || 0)}
                      color="primary"
                      indeterminate={
                        selectedUsers.length > 0 &&
                        selectedUsers.length < (users?.length || 0)
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Restaurant Id</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Registration date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedUsers.map(user => (
                  <TableRow
                    className={classes.tableRow}
                    hover
                    key={user.username}
                    selected={selectedUsers.indexOf(user.username) !== -1}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUsers.indexOf(user.username) !== -1}
                        color="primary"
                        onChange={event => handleSelectOne(event, user.username)}
                        value="true"
                      />
                    </TableCell>
                    <TableCell>
                      <div className={classes.nameContainer}>
                        <Avatar
                          className={classes.avatar}
                          src={user.avatarUrl}
                        >
                          {getInitials(user.username)}
                        </Avatar>
                        <NavLink to={`/user/${user.username}`}>
                          <Typography variant="body1" className={classes.textLink}>{user.username}</Typography>
                        </NavLink>
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                       {user.role}
                    </TableCell>
                <TableCell>
                  <Typography color="primary">{user.restaurantId} </Typography>
                  {userRestaurants(user)}
                </TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>
                      {getTimeString(user.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          </div>
        )}
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={visibleUsers.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </CardActions>
    </Card>
    </Grid>
  );
};

UsersTable.propTypes = {
  className: PropTypes.string,
  users: PropTypes.array.isRequired
};

export default UsersTable;
